import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { formatPhone } from "../me-ui";

// Settings → User Management + Roles & Permissions (docs/16 §B, D33). Admin-only
// (RLS enforces manage_users_roles server-side; this also gates the UI). The
// permission matrix is EDITABLE data — editing a cell writes role_permissions and
// RLS reads it live (no migration). See migration 0026.

// The capability registry — mirrors the seed in 0026. `own` = record-scoped
// (owner/assignee). `enforced` = already wired into RLS this slice; the rest are
// stored/editable now and enforced as their surfaces get built.
const CAPS: { key: string; label: string; own: boolean; enforced: boolean }[] = [
  { key: "view_leads", label: "View leads", own: true, enforced: true },
  { key: "edit_leads", label: "Edit leads", own: true, enforced: true },
  { key: "reassign_leads", label: "Reassign / delegate leads", own: false, enforced: false },
  { key: "import_lists", label: "Import lists (list-stacking)", own: false, enforced: false },
  { key: "view_deals", label: "View deals", own: true, enforced: true },
  { key: "edit_deal", label: "Edit deal / underwriting", own: true, enforced: true },
  { key: "advance_deal_stage", label: "Advance deal stage", own: true, enforced: false },
  { key: "record_offer", label: "Record offer / offer status", own: false, enforced: false },
  { key: "void_contract", label: "Void / cancel contract", own: false, enforced: false },
  { key: "view_edit_buyers", label: "View / edit buyers", own: true, enforced: true },
  { key: "run_dispo_blasts", label: "Run disposition blasts", own: false, enforced: false },
  { key: "send_comms", label: "Send comms (call/SMS/email)", own: true, enforced: false },
  { key: "listen_recordings", label: "Listen to call recordings", own: true, enforced: false },
  { key: "view_pii", label: "View PII (owner contact/financial)", own: false, enforced: false },
  { key: "manage_campaigns_automations", label: "Manage campaigns & automations", own: false, enforced: true },
  { key: "manage_templates_tags", label: "Manage templates & tags", own: false, enforced: true },
  { key: "manage_users_roles", label: "Manage users & roles", own: false, enforced: true },
  { key: "manage_phone", label: "Manage phone numbers & routing", own: false, enforced: true },
  { key: "view_reports", label: "View reports / KPIs", own: true, enforced: false },
  { key: "export_data", label: "Export data", own: false, enforced: false },
];

type Role = { id: number; key: string; name: string };
type UserRow = { id: string; full_name: string; email: string; role_id: number; is_active: boolean };
type Phone = { id: string; e164: string; assigned_user_id: string | null; is_active: boolean };
type Perm = { role_key: string; capability: string; level: string };
const LEVELS = ["full", "own", "none"];
const levelBadge: Record<string, string> = {
  full: "bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300",
  own: "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300",
  none: "bg-muted text-muted-foreground",
};

const UsersPage = () => {
  const notify = useNotify();
  const [tab, setTab] = useState<"users" | "roles">("users");
  const [myLevel, setMyLevel] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [phones, setPhones] = useState<Phone[]>([]);
  const [perms, setPerms] = useState<Record<string, string>>({}); // `${role}:${cap}` -> level
  const [invEmail, setInvEmail] = useState("");
  const [invRole, setInvRole] = useState<number>(2);

  const load = async () => {
    const sb = getSupabaseClient();
    const [{ data: lvl }, { data: r }, { data: u }, { data: ph }, { data: p }] = await Promise.all([
      sb.rpc("capability_level", { p_cap: "manage_users_roles" }),
      sb.from("roles").select("*").order("id"),
      sb.from("users").select("id,full_name,email,role_id,is_active").order("full_name"),
      sb.from("phone_numbers").select("id,e164,assigned_user_id,is_active").order("e164"),
      sb.from("role_permissions").select("role_key,capability,level"),
    ]);
    setMyLevel((lvl as string) ?? "none");
    setRoles((r as Role[]) ?? []);
    setUsers((u as UserRow[]) ?? []);
    setPhones((ph as Phone[]) ?? []);
    const m: Record<string, string> = {};
    (p as Perm[] ?? []).forEach((x) => (m[`${x.role_key}:${x.capability}`] = x.level));
    setPerms(m);
  };
  useEffect(() => { load(); }, []);

  const isAdmin = myLevel === "full";

  // ---- Users ops ----
  const setRole = async (u: UserRow, role_id: number) => {
    const { error } = await getSupabaseClient().from("users").update({ role_id }).eq("id", u.id);
    if (error) notify(`Failed: ${error.message}`, { type: "error" }); else { notify("Role updated", { type: "success" }); load(); }
  };
  const setActive = async (u: UserRow, is_active: boolean) => {
    const { error } = await getSupabaseClient().from("users").update({ is_active }).eq("id", u.id);
    if (error) notify(`Failed: ${error.message}`, { type: "error" }); else { notify(is_active ? "Reactivated" : "Deactivated", { type: "success" }); load(); }
  };
  const assignPhone = async (phoneId: string, userId: string | null) => {
    const { error } = await getSupabaseClient().from("phone_numbers").update({ assigned_user_id: userId }).eq("id", phoneId);
    if (error) notify(`Failed: ${error.message}`, { type: "error" }); else { notify("Phone assignment updated", { type: "success" }); load(); }
  };
  const invite = async () => {
    if (!invEmail.trim()) return;
    const { data, error } = await getSupabaseClient().functions.invoke("admin-invite-user", {
      body: { email: invEmail.trim(), role_id: invRole },
    });
    if (error || (data as any)?.error) { notify(`Invite failed: ${error?.message || (data as any).error}`, { type: "error" }); return; }
    const temp = (data as any)?.temp_password;
    notify(temp ? `Invited ${invEmail}. Temp password: ${temp} (share it; they reset on first login)` : `Invited ${invEmail}`, { type: "success", autoHideDuration: 12000 });
    setInvEmail(""); load();
  };

  // ---- Matrix op ----
  const setCell = async (role_key: string, capability: string, level: string) => {
    setPerms((m) => ({ ...m, [`${role_key}:${capability}`]: level }));
    const { error } = await getSupabaseClient().from("role_permissions").upsert(
      { role_key, capability, level }, { onConflict: "role_key,capability" });
    if (error) { notify(`Failed: ${error.message}`, { type: "error" }); load(); }
  };

  const phonesFor = (userId: string) => phones.filter((p) => p.assigned_user_id === userId);
  const unassignedPhones = phones.filter((p) => !p.assigned_user_id);

  if (myLevel !== null && !isAdmin) {
    return (
      <div className="p-2 max-w-2xl">
        <h1 className="text-xl font-semibold">User Management</h1>
        <Card className="mt-4"><CardContent className="py-10 text-center text-muted-foreground">
          Admins only. Your role doesn't have "Manage users & roles".
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-2 max-w-6xl">
      <h1 className="text-xl font-semibold">User Management</h1>
      <div className="text-sm text-muted-foreground mb-4">Manage users, roles, and the editable permission matrix. Changes take effect immediately (RLS reads the matrix live).</div>

      <div className="flex gap-1 mb-4">
        <Button variant={tab === "users" ? "default" : "outline"} size="sm" onClick={() => setTab("users")}>Users</Button>
        <Button variant={tab === "roles" ? "default" : "outline"} size="sm" onClick={() => setTab("roles")}>Roles &amp; Permissions</Button>
      </div>

      {tab === "users" && (
        <>
          <Card className="mb-4">
            <CardHeader><CardTitle className="text-base">Invite a user</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Email</span>
                <Input className="w-64" type="email" value={invEmail} placeholder="teammate@idem-ventures.com" onChange={(e) => setInvEmail(e.target.value)} /></div>
              <div className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Role</span>
                <select className="border rounded px-2 py-2 text-sm bg-background" value={invRole} onChange={(e) => setInvRole(+e.target.value)}>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select></div>
              <Button onClick={invite}>Invite</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Users <span className="text-xs font-normal text-muted-foreground">({users.length})</span></CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {users.map((u) => (
                <div key={u.id} className="flex flex-wrap items-center gap-3 border-b pb-2 last:border-0">
                  <div className="min-w-[180px]">
                    <div className="font-medium">{u.full_name}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </div>
                  <div className="flex flex-col gap-0.5"><span className="text-[11px] text-muted-foreground">Role</span>
                    <select className="border rounded px-2 py-1 text-sm bg-background" value={u.role_id} onChange={(e) => setRole(u, +e.target.value)}>
                      {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select></div>
                  <label className="flex items-center gap-2"><Switch checked={u.is_active} onCheckedChange={(v) => setActive(u, v)} /><span className="text-xs">{u.is_active ? "Active" : "Inactive"}</span></label>
                  <div className="flex items-center gap-1 ml-auto">
                    {phonesFor(u.id).map((p) => (
                      <Badge key={p.id} variant="outline" className="gap-1">{formatPhone(p.e164)}
                        <button className="ml-1 text-muted-foreground hover:text-foreground" title="Unassign" onClick={() => assignPhone(p.id, null)}>×</button>
                      </Badge>
                    ))}
                    {unassignedPhones.length > 0 && (
                      <select className="border rounded px-2 py-1 text-xs bg-background" value="" onChange={(e) => e.target.value && assignPhone(e.target.value, u.id)}>
                        <option value="">+ assign #</option>
                        {unassignedPhones.map((p) => <option key={p.id} value={p.id}>{formatPhone(p.e164)}</option>)}
                      </select>
                    )}
                  </div>
                </div>
              ))}
              {users.length === 0 && <div className="text-sm text-muted-foreground">No users.</div>}
            </CardContent>
          </Card>
        </>
      )}

      {tab === "roles" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Roles &amp; Permissions</CardTitle>
            <div className="text-xs text-muted-foreground mt-1">
              Each cell: <Badge className={levelBadge.full}>full</Badge> all records · <Badge className={levelBadge.own}>own</Badge> assigned only · <Badge className={levelBadge.none}>none</Badge>. A dot marks capabilities already enforced by RLS; the rest are stored now, enforced as their modules ship.
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="text-sm border-collapse min-w-[720px]">
              <thead>
                <tr>
                  <th className="text-left font-medium p-2 sticky left-0 bg-background">Capability</th>
                  {roles.map((r) => <th key={r.id} className="p-2 text-center font-medium whitespace-nowrap">{r.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {CAPS.map((c) => (
                  <tr key={c.key} className="border-t">
                    <td className="p-2 sticky left-0 bg-background whitespace-nowrap">
                      {c.enforced && <span className="text-green-600 mr-1" title="Enforced by RLS now">●</span>}
                      {c.label}
                    </td>
                    {roles.map((r) => {
                      const cur = perms[`${r.key}:${c.key}`] ?? "none";
                      const opts = c.own ? LEVELS : LEVELS.filter((l) => l !== "own");
                      return (
                        <td key={r.id} className="p-1 text-center">
                          <select className={`rounded px-1.5 py-0.5 text-xs border ${levelBadge[cur]}`} value={cur} onChange={(e) => setCell(r.key, c.key, e.target.value)}>
                            {opts.map((l) => <option key={l} value={l}>{l}</option>)}
                          </select>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

UsersPage.path = "/settings/users";
export default UsersPage;
